import { z } from 'zod'
import { DEFAULT_ESTIMATE_MINUTES, DEFAULT_ESTIMATE_UNTYPED } from './estimates'
import { ESTIMATE_MINUTES_MAX, ESTIMATE_MINUTES_MIN, REMINDER_INTERVAL_WORKING_DAYS } from './plan'
import { priorityRulesSchema } from './priority-rules'
import { AWAY_SOON_DAYS } from './timeoff'

/**
 * The user's rules: the numbers hugo decides with, each the user's to change (Settings → Rules).
 * Their defaults are what hugo did before rules existed. The planner, the heads-ups and the
 * Outbox read them from the settings; nothing decides with a number of its own.
 */

const minutes = z.number().int().min(ESTIMATE_MINUTES_MIN).max(ESTIMATE_MINUTES_MAX)

/** Time needed when the user has not said, by type of work (and for untyped items). */
export const estimateDefaultsSchema = z.object({
  reply: minutes.default(DEFAULT_ESTIMATE_MINUTES.reply),
  decision: minutes.default(DEFAULT_ESTIMATE_MINUTES.decision),
  admin: minutes.default(DEFAULT_ESTIMATE_MINUTES.admin),
  review: minutes.default(DEFAULT_ESTIMATE_MINUTES.review),
  meeting: minutes.default(DEFAULT_ESTIMATE_MINUTES.meeting),
  other: minutes.default(DEFAULT_ESTIMATE_MINUTES.other),
  fix: minutes.default(DEFAULT_ESTIMATE_MINUTES.fix),
  deliverable: minutes.default(DEFAULT_ESTIMATE_MINUTES.deliverable),
  untyped: minutes.default(DEFAULT_ESTIMATE_UNTYPED),
})
export type EstimateDefaults = z.output<typeof estimateDefaultsSchema>

const reminderInterval = z.number().int().min(1).max(20)
const awaySoon = z.number().int().min(0).max(60)
const timeOffNotice = z.number().int().min(1).max(60)
const undatedNotice = z.number().int().min(1).max(20)
const focusBlock = z.number().int().min(15).max(240).multipleOf(15)

export const userRulesSchema = z.object({
  estimateMinutes: estimateDefaultsSchema.default(() => estimateDefaultsSchema.parse({})),
  /** After a reminder, the next follow-up this many working days later. */
  reminderIntervalWorkingDays: reminderInterval.default(REMINDER_INTERVAL_WORKING_DAYS),
  /** The header says "Away from …" this many days before time off (0: only while away). */
  awaySoonDays: awaySoon.default(AWAY_SOON_DAYS),
  /** People the user waits on hear about time off starting within this many days. */
  timeOffNoticeDays: timeOffNotice.default(21),
  /** …or, without a follow-up date, when it starts within this many working days. */
  undatedNoticeWorkingDays: undatedNotice.default(5),
  /** Focus time counts free stretches of at least this many minutes. */
  focusBlockMinutes: focusBlock.default(60),
  /** Rules that set the priority of items the user has not set (see priority-rules.ts). */
  priorityRules: priorityRulesSchema.default([]),
})
export type UserRules = z.output<typeof userRulesSchema>

export const defaultRules: UserRules = userRulesSchema.parse({})

/** A change to the rules: any of them, nothing filled in, so the others stay as they are. */
export const rulesPatchSchema = z.object({
  estimateMinutes: z
    .object({
      reply: minutes,
      decision: minutes,
      admin: minutes,
      review: minutes,
      meeting: minutes,
      other: minutes,
      fix: minutes,
      deliverable: minutes,
      untyped: minutes,
    })
    .partial()
    .optional(),
  reminderIntervalWorkingDays: reminderInterval.optional(),
  awaySoonDays: awaySoon.optional(),
  timeOffNoticeDays: timeOffNotice.optional(),
  undatedNoticeWorkingDays: undatedNotice.optional(),
  focusBlockMinutes: focusBlock.optional(),
  /** The whole list, in order (the first that matches wins). */
  priorityRules: priorityRulesSchema.optional(),
})
export type RulesPatch = z.output<typeof rulesPatchSchema>

/** The rules after a change: what the change names, the rest as they were. */
export function mergeRules(current: UserRules, patch: RulesPatch): UserRules {
  return {
    estimateMinutes: { ...current.estimateMinutes, ...patch.estimateMinutes },
    reminderIntervalWorkingDays:
      patch.reminderIntervalWorkingDays ?? current.reminderIntervalWorkingDays,
    awaySoonDays: patch.awaySoonDays ?? current.awaySoonDays,
    timeOffNoticeDays: patch.timeOffNoticeDays ?? current.timeOffNoticeDays,
    undatedNoticeWorkingDays: patch.undatedNoticeWorkingDays ?? current.undatedNoticeWorkingDays,
    focusBlockMinutes: patch.focusBlockMinutes ?? current.focusBlockMinutes,
    priorityRules: patch.priorityRules ?? current.priorityRules,
  }
}
